import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DeviceService } from '@omni/services/device/device.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { ResourceService } from '@omni/services/resource/resource.service';
import { PresentationView } from '@omni/services/ui/ui.service';
import { AdoptionLadderJourneyService } from '@omni/services/adoption-ladder-journey/adoption-ladder-journey.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { PopoverController } from '@ionic/angular';
import { ListPreviewComponent } from '../list-preview/list-preview.component';
// NOTE: PresentationPreviewComponent is intentionally NOT imported here. It is navigated to by
// PageName via NavigationService (resolved from the lazy component-registry) to avoid an eager
// import cycle through SharedModule — see _openContentDetails.
import { ResourceDetailsComponent } from '@omni/components/resource/resource-details/resource-details';
import {
  JourneySuggestionType, PerformanceMetricDisplay, ProductWithAdoptionLadder,
  JourneySuggestionCategory, ContentItem,
  LadderDataPerType, LadderHistoryEntry, LadderStage, ProductWithMultipleLadders,
} from '@omni/classes/customer-segment/adoption-ladder.class';
import { Period, Metric } from '@omni/services/contact-spotlight/rxsalesdata.types';
import { PdrpOptOutStatus } from '@omni/classes/contact/contact.class';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { RxProductSeries } from './rx-trends/rx-trends.types';
import { RxTrendsDataService, RxTrendSeriesDocument } from '@omni/data-services/rx-trends/rx-trends.data.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { Brand } from '@omni/classes/brand/brand.class';
import { CaseManagementService } from '@omni/services/case-management/case-management.service';
import {
  buildMonthlyLabelsFromAnchor,
  buildWeeklyLabelsFromAnchor,
} from './rx-trends/rx-trends.adapter';
import { isEmpty, uniqBy } from 'lodash-es';

import { _ } from '@omni/utility/lodash-chain';
import {
  compareRxProductsForDisplay,
  normalizeProductName,
  resolveRxSeriesKey,
  RX_ALL_PRODUCTS_ID,
} from './rx-trends/rx-trends.aggregate';

@Component({
  selector: 'adoption-ladder-journey',
  templateUrl: 'adoption-ladder-journey.html',
  styleUrls: ['adoption-ladder-journey.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdoptionLadderJourneyComponent implements OnInit, OnChanges, OnDestroy {

  @Input() rawSuggestionData: any;
  @Input() rawRxSalesData: any;
  @Input() rawRxSalesDataRealTimeFetched: any;
  @Input() entityType: string = 'contact';
  @Input() entityId: string = '';

  JourneySuggestionType = JourneySuggestionType;

  // Product lists
  productsWithMultipleLadders: ProductWithMultipleLadders[] = [];
  productsFromRxSalesData: ProductWithAdoptionLadder[] = [];
  showEmptyProductMessage: boolean = false;

  // Active ladder state
  activeLadderTypeId: string = '';
  currentLadderHistory: LadderHistoryEntry[] = [];
  pendingStage: LadderStage | null = null;
  showHistoryView: boolean = false;

  // Suggestion + ladder display state
  hasActiveAdoptionLadderInProductRating: boolean = false;
  suggestionDataToDisplay: JourneySuggestionCategory[] = [];
  hasSuggestionsDataInProductRating: boolean = false;

  // Performance metrics
  selectedDuration: Period = Period.CW;
  performanceMetricsToDisplay: PerformanceMetricDisplay[] = [];
  structuredPerformanceData: any = {};
  hasPerformanceMetrics: boolean = false;
  durationOptions = _(Period).filter((period: any) => !isNaN(Number(period))).map((period: any) => ({ value: period, label: Period[period] })).value();

  rawAdoptionLadderLevel: any[] = [];
  rawContactJourneySuggestionData: any[] = [];
  rawContactRxSalesDataRealTimeFetched: any[] = [];
  rawContactRxSalesData: any[] = [];
  /**
   * Rx weekly/monthly trend series for the current contact, keyed by product.
   *
   * Assigned once per data load rather than exposed as a getter, so the OnPush
   * child does not see a fresh array reference on every change-detection pass.
   */
  rxTrendsSeries: RxProductSeries[] = [];
  /**
   * Source extract date behind the series, normalised to `YYYY-MM-DD`.
   *
   * This is when the warehouse last loaded, not the last day the figures
   * cover — the newest bucket can end weeks earlier. Coverage is read off the
   * chart's own axis labels; this says whether the snapshot itself is current.
   */
  rxTrendsDataAsOfDate: string | null = null;
  /** True while the series read is in flight, so the card can hold a placeholder. */
  rxTrendsLoading = false;
  /** True when this contact is PDRP-opted-out or Rx-sales-data-access-restricted. */
  rxTrendsRestricted = false;
  rxTrendsRestrictionMessage = '';
  /**
   * True while the territory's Rx trend ingest is still paging.
   *
   * Mirrors the data service rather than being per-contact: the ingest covers
   * the whole territory, and this contact's document appears only once the page
   * carrying it lands.
   */
  rxTrendsIngestInProgress = false;
  /** Documents on disk from an ingest that never finished — see isSnapshotPartial(). */
  rxTrendsSnapshotPartial = false;
  rawProductSegmentations: any[] = [];
  mapSuggestionDataToStructuredFormat: { [foundProductRatingId: string]: any } = {};
  dataLoaded: boolean = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly presentationService: PresentationService,
    private readonly deviceService: DeviceService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly navService: NavigationService,
    private readonly resourceService: ResourceService,
    private readonly adoptionLadderJourneyService: AdoptionLadderJourneyService,
    private readonly contactService: ContactOfflineService,
    public popoverController: PopoverController,
    private readonly cd: ChangeDetectorRef,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly rxTrendsDataService: RxTrendsDataService,
    private readonly brandService: BrandOfflineService,
    private readonly caseManagementService: CaseManagementService,
    private readonly authService: AuthenticationService,
  ) {}

  ngOnInit() {
    this.initializeAllData();
    this.watchContactForRestrictionChanges();
    this.watchRxTrendsIngest();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entityId'] && !changes['entityId'].firstChange && changes['entityId'].currentValue) {
      this.resetState();
      this.initializeAllData();
    } else if (
      (changes['rawSuggestionData'] && !changes['rawSuggestionData'].firstChange) ||
      (changes['rawRxSalesDataRealTimeFetched'] && !changes['rawRxSalesDataRealTimeFetched'].firstChange) ||
      (changes['rawRxSalesData'] && !changes['rawRxSalesData'].firstChange)
    ) {
      if (this.dataLoaded && !isEmpty(this.productsWithMultipleLadders)) {
        if (changes['rawSuggestionData']) {
          this.rawContactJourneySuggestionData = this.rawSuggestionData || [];
          this.mapSuggestionDataToStructuredFormat = this.mapRawSuggestionDataToStructuredFormat(this.rawContactJourneySuggestionData);
          this.updateDashboardData();
        }
        if (changes['rawRxSalesDataRealTimeFetched']) {
          this.rawContactRxSalesDataRealTimeFetched = this.rawRxSalesDataRealTimeFetched || [];
          this.productsFromRxSalesData = this.mapRealTimeFetchedRxSalesDataToProducts(this.rawContactRxSalesDataRealTimeFetched);
          this.structuredPerformanceData = this.mapFormattedRxSalesDataRealTimeFetched();
        } else if (changes['rawRxSalesData']) {
          this.rawContactRxSalesData = this.rawRxSalesData || [];
          this.productsFromRxSalesData = this.mapRxSalesDataToProducts(this.rawContactRxSalesData);
          this.structuredPerformanceData = this.mapFormattedRxSalesData();
        }
        const first = this.selectedProduct || this.productsWithMultipleLadders[0];
        if (first) this.performanceMetricsToDisplay = this.mapPerformanceMetricsToDisplay(this.structuredPerformanceData, this.selectedDuration, first.id);
        this.cd.markForCheck();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetState();
  }

  private resetState() {
    this.productsWithMultipleLadders = [];
    this.productsFromRxSalesData = [];
    this.rawProductSegmentations = [];
    this.rawContactJourneySuggestionData = [];
    this.rawContactRxSalesData = [];
    this.rawContactRxSalesDataRealTimeFetched = [];
    this.mapSuggestionDataToStructuredFormat = {};
    this.structuredPerformanceData = {};
    this.activeLadderTypeId = '';
    this.currentLadderHistory = [];
    this.pendingStage = null;
    this.showHistoryView = false;
    this.showEmptyProductMessage = false;
    this.hasActiveAdoptionLadderInProductRating = false;
    this.hasSuggestionsDataInProductRating = false;
    this.hasPerformanceMetrics = false;
    this.dataLoaded = false;
    // Cleared with the rest: the series read is async, so leaving these would
    // render the previous contact's prescription figures until it resolves.
    this.rxTrendsSeries = [];
    this.rxTrendsDataAsOfDate = null;
    this.rxTrendsLoading = false;
    this.rxTrendsRestricted = false;
    this.rxTrendsRestrictionMessage = '';
  }

  // ── Initialization ───────────────────────────────────────────────────────────

  private async initializeAllData() {
    const requestedEntityId = this.entityId;

    // Computed synchronously, before the async work below starts, so it's
    // never racing loadRxTrendsSeries()'s continuation into
    // mergeRxTrendsIntoProductPills() — that race is fine between two things
    // that both build the pill row, but restriction must always win it.
    if (this.entityType === 'contact') {
      this.computeRxTrendsRestriction(this.entityId);
    }

    // Fired here, not after the awaits below: the Rx trends read is its own
    // disk-backed lookup with no dependency on segmentation/suggestion/sales
    // data, so waiting for those first only delays `rxTrendsLoading` turning
    // true — during which `<rx-trends>` isn't rendered at all (see its `*ngIf`
    // in the template) and the card's own skeleton never gets a chance to
    // show. Starting it in parallel is what makes the skeleton the first
    // thing a rep sees instead of blank space.
    void this.loadRxTrendsSeries();

    this.rawAdoptionLadderLevel = await this.adoptionLadderJourneyService.getAdoptionLadderData();

    if (this.entityType === 'contact' || this.entityType === 'account') {
      const isContact = this.entityType === 'contact';
      // Offline: leave rawProductSegmentations empty rather than falling back to the locally
      // cached contact.productSegmentations — ladder/stage data can change server-side, and
      // showing a possibly-stale ladder offline risks a discrepancy once back online. The
      // dataLoaded && isOffline && productsWithMultipleLadders.length === 0 branch in the
      // template renders JOURNEY_DATA_OFFLINE_MSG instead.
      //
      // Fetched into locals and committed in one step under the entity check
      // below: the pane switches entity on the same component instance, so an
      // earlier entity's fetch can resolve after a later one and would
      // otherwise leave its rows in these fields for the next
      // assembleProductPills() to render under the wrong name (OMNI-62226).
      let segmentations: any[] = [];
      let suggestions: any[] = [];
      if (!this.deviceService.isOffline) {
        segmentations = isContact
          ? await this.adoptionLadderJourneyService.fetchContactCustomerSegmentationData(requestedEntityId)
          : await this.adoptionLadderJourneyService.fetchAccountCustomerSegmentationData(requestedEntityId);
        // always fetch fresh so recent additions/updates are visible
        suggestions = isContact
          ? await this.adoptionLadderJourneyService.getContactJourneySuggestionData(requestedEntityId)
          : await this.adoptionLadderJourneyService.getAccountJourneySuggestionData(requestedEntityId);
      }

      let rxSalesRealTimeFetched: any[] = this.rawRxSalesDataRealTimeFetched;
      let rxSalesFromDatabase: any[] = [];
      if (isEmpty(rxSalesRealTimeFetched) && !isEmpty(this.rawRxSalesData)) {
        rxSalesFromDatabase = this.rawRxSalesData;
      } else if (isEmpty(rxSalesRealTimeFetched)) {
        rxSalesRealTimeFetched = isContact
          ? await this.adoptionLadderJourneyService.getContactRxSalesData(requestedEntityId)
          : await this.adoptionLadderJourneyService.getAccountRxSalesData(requestedEntityId);
      }

      if (this.entityId !== requestedEntityId) return;

      this.rawProductSegmentations = segmentations;
      this.rawContactJourneySuggestionData = suggestions;
      if (!isEmpty(rxSalesRealTimeFetched)) {
        this.rawContactRxSalesDataRealTimeFetched = rxSalesRealTimeFetched;
        this.productsFromRxSalesData = this.mapRealTimeFetchedRxSalesDataToProducts(rxSalesRealTimeFetched);
      } else if (!isEmpty(rxSalesFromDatabase)) {
        this.rawContactRxSalesData = rxSalesFromDatabase;
        this.productsFromRxSalesData = this.mapRxSalesDataToProducts(rxSalesFromDatabase);
      }
    }

    this.assembleProductPills();
  }

  /**
   * Builds the pill row and its dependent display state from data already in
   * memory, so a restriction verdict that lands after the initial load can be
   * applied without refetching anything.
   */
  private assembleProductPills(): void {
    this.productsWithMultipleLadders = this.mapProductsWithMultipleLadders(this.rawProductSegmentations);
    this.sortProductPills();

    // Include products that have Rx sales data but no segmentation/ladder records.
    // Skipped when restricted — same reasoning as mergeRxTrendsIntoProductPills():
    // a pill that exists only for now-hidden Rx figures has nothing to show.
    if (!isEmpty(this.productsFromRxSalesData) && !this.rxTrendsRestricted) {
      const existingIds = new Set(this.productsWithMultipleLadders.map(p => p.id));
      const rxOnlyProducts: ProductWithMultipleLadders[] = this.productsFromRxSalesData
        .filter(p => !existingIds.has(p.id))
        .map(p => ({ id: p.id, name: p.name, ladders: [], selected: false }));
      if (rxOnlyProducts.length > 0) {
        this.productsWithMultipleLadders = [...this.productsWithMultipleLadders, ...rxOnlyProducts];
        this.sortProductPills();
      }
    }

    if (!isEmpty(this.productsWithMultipleLadders)) {
      this.showEmptyProductMessage = false;
      this.productsWithMultipleLadders[0].selected = true;
      const firstLadder = this.visibleLaddersForSelectedProduct[0];
      this.activeLadderTypeId = firstLadder?.ladderTypeId || '';
      this.currentLadderHistory = firstLadder?.history || [];

      this.mapSuggestionDataToStructuredFormat = this.mapRawSuggestionDataToStructuredFormat(this.rawContactJourneySuggestionData);
      this.updateDashboardData();

      if (!isEmpty(this.rawContactRxSalesDataRealTimeFetched)) {
        this.structuredPerformanceData = this.mapFormattedRxSalesDataRealTimeFetched();
      } else if (!isEmpty(this.rawContactRxSalesData)) {
        this.structuredPerformanceData = this.mapFormattedRxSalesData();
      }
      const first = this.productsWithMultipleLadders[0];
      this.performanceMetricsToDisplay = this.mapPerformanceMetricsToDisplay(this.structuredPerformanceData, Period.CW, first.id);
    } else {
      this.showEmptyProductMessage = true;
    }
    this.mergeRxTrendsIntoProductPills();
    this.dataLoaded = true;
    this.cd.markForCheck();
  }

  // ── Getters ──────────────────────────────────────────────────────────────────

  get selectedProduct(): ProductWithMultipleLadders | undefined {
    return this.productsWithMultipleLadders?.find(p => p.selected);
  }

  get hasRxTrends(): boolean {
    return !isEmpty(this.rxTrendsSeries);
  }

  /**
   * Whether the card can be trusted to show everything this contact has.
   *
   * False with no series yet and either the ingest still running or the stored
   * territory left partial by one that abandoned — both are "not here yet"
   * rather than "no prescriptions". Series already on disk keep rendering
   * through an ingest: they are a whole snapshot for this contact, just
   * possibly the previous one, and blanking them out for the length of a
   * territory pull would be worse than figures that are a refresh behind.
   */
  get rxTrendsDataComplete(): boolean {
    return this.hasRxTrends || !(this.rxTrendsIngestInProgress || this.rxTrendsSnapshotPartial);
  }

  /**
   * Mirrors the ingest state, and re-reads this contact's series when it
   * finishes, so a chart that was missing mid-ingest fills itself in instead of
   * waiting for the rep to leave the tab and come back.
   */
  private watchRxTrendsIngest(): void {
    this.rxTrendsDataService.isIngestInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((inProgress) => {
        const hasFinished = this.rxTrendsIngestInProgress && !inProgress;
        this.rxTrendsIngestInProgress = inProgress;

        if (hasFinished && this.entityType === 'contact' && this.entityId) {
          void this.loadRxTrendsSeries();
        }
        this.cd.markForCheck();
      });
  }

  /**
   * Reads this contact's Rx trend series from the local DB.
   *
   * Async because the read is disk-backed; the assignment plus `markForCheck`
   * is what makes the card appear once it lands. Every interaction after that —
   * pill tap, period toggle, legend chip — is a slice of this already-loaded
   * data and costs no further I/O.
   *
   * Bucket labels are rebuilt here rather than read from the document, so they
   * follow the user's date settings instead of whatever was current at ingest.
   */
  private async loadRxTrendsSeries(): Promise<void> {
    if (!this.authService.hasFeatureAction(FeatureActionsMap.RX_TRENDS)) return;
    const requestedEntityId = this.entityId;
    this.rxTrendsLoading = true;
    this.cd.markForCheck();

    let document: RxTrendSeriesDocument | null = null;
    try {
      document = await this.rxTrendsDataService.getSeriesForContact(
        this.resolveContactMdmId(requestedEntityId),
      );
    } finally {
      // Cleared even on failure, so a read that throws leaves the card in its
      // empty state rather than under a placeholder that never resolves.
      if (this.entityId === requestedEntityId) {
        this.rxTrendsLoading = false;
      }
    }

    // Switching contacts quickly can land an earlier read after a later one.
    // Without this, contact A's figures would overwrite contact B's.
    if (this.entityId !== requestedEntityId) return;

    this.rxTrendsDataAsOfDate = document?.dataAsOfDate
      ? this.dateTimeFormatsService.formatDateForDisplay(document.dataAsOfDate)
      : null;

    const eligibleSeries = (document?.series ?? []).filter(
      (series) => !!this.resolveProductCatalogEntry(series.productId, series.productName),
    );
    const droppedCount = (document?.series?.length ?? 0) - eligibleSeries.length;
    if (droppedCount > 0) {
      console.warn(
        `[rx-trends] dropped ${droppedCount} product series not mapped to this position`,
      );
    }

    const storedSeries = eligibleSeries.map((series) => ({
      ...series,
      // Resolved here rather than in the card: the catalogs that know which
      // products are a competitor's live on this side, and the Rx card needs it
      // to keep competitor prescriptions out of the All-products total.
      isCompetitor: this.resolveIsCompetitorProduct(series.productId, series.productName),
      weeklyLabels: buildWeeklyLabelsFromAnchor(
        series.weekAnchor,
        series.weekly?.NRx?.length ?? 0,
        (weekStartDate, previousWeekStartDate) => {
          const label = this.dateTimeFormatsService.formatDate(weekStartDate, 'MMM D');
          const yearChanged =
            !previousWeekStartDate || previousWeekStartDate.slice(0, 4) !== weekStartDate.slice(0, 4);
          return yearChanged ? `${label} ${weekStartDate.slice(0, 4)}` : label;
        },
      ),
      monthlyLabels: buildMonthlyLabelsFromAnchor(
        series.monthAnchor,
        series.monthly?.NRx?.length ?? 0,
        (monthKey, previousMonthKey) => {
          const label = this.dateTimeFormatsService.formatDate(`${monthKey}-01`, 'MMM');
          const yearChanged = !previousMonthKey || previousMonthKey.slice(0, 4) !== monthKey.slice(0, 4);
          return yearChanged ? `${label} ${monthKey.slice(0, 4)}` : label;
        },
      ),
    }));

    this.rxTrendsSeries = storedSeries;
    this.mergeRxTrendsIntoProductPills();
    this.cd.markForCheck();

    // Only when this contact has nothing, and after the render rather than
    // before it: the answer only ever swaps one empty-state message for
    // another, and it costs an index scan.
    if (isEmpty(storedSeries)) {
      this.rxTrendsSnapshotPartial = await this.rxTrendsDataService.isSnapshotPartial();
      this.cd.markForCheck();
    }
  }

  /**
   * Adds the Rx response's products to the pill row, plus an `All` pill.
   *
   * Runs from both the product-assembly path and the series read, because the
   * two race — whichever lands second is the one that does the work.
   *
   * Matched on the Dynamics product id where the payload carries one, on name
   * where it does not — it is populated on only some rows today. Drop the name
   * leg once every row has an id.
   */
  private mergeRxTrendsIntoProductPills(): void {
    // Restricted: the card only ever shows the PDRP message regardless of
    // which pill is selected, so injecting an "All" pill or Rx-only product
    // pills here would just add dead entries with nothing to show.
    if (isEmpty(this.rxTrendsSeries) || this.rxTrendsRestricted) return;

    const pills = this.productsWithMultipleLadders.filter(p => p.id !== RX_ALL_PRODUCTS_ID);
    const pilledIds = new Set(pills.map(p => p.id));
    const pilledNames = new Set(pills.map(p => normalizeProductName(p.name)));

    const rxOnlyProducts: ProductWithMultipleLadders[] = this.rxTrendsSeries
      .filter(
        series =>
          !pilledIds.has(series.productId) &&
          !pilledNames.has(normalizeProductName(series.productName)),
      )
      .map(series => ({ id: series.productId, name: series.productName, ladders: [], selected: false }));

    const products = [...pills, ...rxOnlyProducts];

    this.productsWithMultipleLadders = [
      {
        id: RX_ALL_PRODUCTS_ID,
        name: this.translate.instant('RX_TRENDS_ALL_PRODUCTS'),
        ladders: [],
        selected: false,
      },
      ...products,
    ];
    this.sortProductPills();

    // The leading pill after sorting, not `products[0]`: that array is in
    // arrival order, so it would open on whichever product the payload happened
    // to list first rather than on the one the rep sees first.
    const firstProductPill = this.ownProductPills[0] ?? this.competitorProductPills[0];
    if (!products.some(p => p.selected) && firstProductPill) {
      this.selectProduct(firstProductPill);
    }
    this.showEmptyProductMessage = false;
  }

  /**
   * Orders the pill row: the pinned All pill, then ours alphabetically, then the
   * competitors' — the same order the All chart draws its lines in.
   *
   * Competitor status is resolved here rather than at pill construction because
   * both assembly paths build pills from sources that don't carry it, and it is
   * what the order depends on.
   */
  private sortProductPills(): void {
    this.productsWithMultipleLadders.forEach(pill => {
      if (pill.id === RX_ALL_PRODUCTS_ID) return;
      pill.isCompetitorProduct = this.resolveIsCompetitorProduct(pill.id, pill.name);
    });

    const isAllPill = (pill: ProductWithMultipleLadders) => pill.id === RX_ALL_PRODUCTS_ID;

    this.productsWithMultipleLadders.sort((a, b) => {
      if (isAllPill(a) || isAllPill(b)) return isAllPill(a) === isAllPill(b) ? 0 : isAllPill(a) ? -1 : 1;
      return compareRxProductsForDisplay(
        { name: a.name, isCompetitor: !!a.isCompetitorProduct },
        { name: b.name, isCompetitor: !!b.isCompetitorProduct },
      );
    });
  }

  /**
   * BrandOfflineService.brands (the products sync) never includes competitor
   * products, so it's checked first for the common case and caseProductSKU
   * (brandskuproducts, gated behind the case-intake/competitor-data FA bundle)
   * is the fallback for products that only exist there.
   */
  private resolveProductCatalogEntry(id: string, name: string): Brand | null {
    const catalogs = [this.brandService.brands, this.caseManagementService.caseProductSKU];
    for (const catalog of catalogs) {
      const match = catalog?.find(b => b.ID === id)
        ?? catalog?.find(b => normalizeProductName(b.name) === normalizeProductName(name));
      if (match) return match;
    }
    return null;
  }

  private resolveIsCompetitorProduct(id: string, name: string): boolean {
    return this.resolveProductCatalogEntry(id, name)?.isCompetitorProduct === 'true';
  }

  get isAllProductsSelected(): boolean {
    return this.selectedProduct?.id === RX_ALL_PRODUCTS_ID;
  }

  get ownProductPills(): ProductWithMultipleLadders[] {
    return this.productsWithMultipleLadders.filter(p => p.id !== RX_ALL_PRODUCTS_ID && !p.isCompetitorProduct);
  }

  get competitorProductPills(): ProductWithMultipleLadders[] {
    return this.productsWithMultipleLadders.filter(p => p.id !== RX_ALL_PRODUCTS_ID && p.isCompetitorProduct);
  }

  /** What the Rx card charts: the summed series, a product's series, or nothing. */
  get selectedRxSeriesKey(): string | null {
    return resolveRxSeriesKey(this.selectedProduct, this.rxTrendsSeries);
  }

  /**
   * PDRP opt-out or the `indskr_restrictrxsalesdataaccess` flag both mean the
   * contact's prescribing figures must not be shown — same rule as the
   * legacy Rx Sales Data check in contact-info.ts/contact-details.ts. Data is
   * still fetched and cached normally; only display is gated, so a restricted
   * HCP's figures remain available for territory-level rollups later.
   *
   * Fails closed: when neither the loaded contact nor the synced list can
   * supply the contact's attributes, the verdict is unknown and the figures
   * stay hidden. `watchContactForRestrictionChanges` reopens them once the
   * contact resolves, so an unknown verdict is never a dead end.
   */
  private computeRxTrendsRestriction(contactId: string): void {
    const raw = this.resolveContactRaw(contactId);
    if (!raw) {
      this.rxTrendsRestricted = true;
      this.rxTrendsRestrictionMessage = this.translate.instant('RX_TRENDS_RESTRICTION_UNVERIFIED');
      return;
    }

    const pdrpOptedOut = raw.omniveev_pdrpoptout === PdrpOptOutStatus.OptedOut
      && this.dateTimeFormatsService.isPastDate(raw.omniveev_pdrpoptoutdate);
    const rxAccessRestricted = !!raw.indskr_restrictrxsalesdataaccess;

    this.rxTrendsRestricted = pdrpOptedOut || rxAccessRestricted;
    this.rxTrendsRestrictionMessage = this.rxTrendsRestricted
      ? this.translate.instant('RX_TRENDS_RESTRICTED')
      : '';
  }

  /**
   * The loaded contact lags `contactInformation` while a contact switch is in
   * flight, and is cleared outright on teardown, so the synced list is the
   * fallback — it carries the same attributes (see
   * spotlight-territory-performance's Rx restriction read).
   */
  private resolveContactRaw(contactId: string): any {
    if (!contactId) return undefined;

    const loadedContact = this.contactService.contactInformation;
    if (loadedContact?.ID === contactId && loadedContact.raw) {
      return loadedContact.raw;
    }

    return this.contactService.contacts?.find((contact) => contact.ID === contactId)?.raw;
  }

  private watchContactForRestrictionChanges(): void {
    this.contactService.contactInformationObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.entityType !== 'contact' || !this.entityId) return;

        const wasRestricted = this.rxTrendsRestricted;
        this.computeRxTrendsRestriction(this.entityId);
        if (this.rxTrendsRestricted === wasRestricted) return;

        if (this.dataLoaded) {
          this.assembleProductPills();
        }
        this.cd.markForCheck();
      });
  }

  /**
   * The Rx payload's `hcp_id` is the contact's MDM id, so that is what keys the
   * series lookup. A globally-searched contact may only be hydrated in one of
   * the two sources, hence the fallback.
   */
  private resolveContactMdmId(contactId: string): string | undefined {
    if (!contactId) return undefined;

    const loadedContact = this.contactService.contactInformation;
    if (loadedContact?.ID === contactId && loadedContact.indskr_mdmid) {
      return loadedContact.indskr_mdmid;
    }

    const syncedContact = this.contactService.contacts?.find((contact) => contact.ID === contactId);
    return syncedContact?.indskr_mdmid || undefined;
  }

  get visibleLaddersForSelectedProduct(): LadderDataPerType[] {
    const product = this.selectedProduct;
    if (!product) return [];
    let ladders = product.ladders.filter(l => l.isVisible);
    // Stakeholder Influence is contacts-only
    if (this.entityType === 'account') {
      ladders = ladders.filter(l => !l.ladderTypeName.toLowerCase().includes('stakeholder'));
    }
    return ladders.sort((a, b) => a.ladderTypeName.localeCompare(b.ladderTypeName));
  }

  get activeLadder(): LadderDataPerType | undefined {
    const ladders = this.visibleLaddersForSelectedProduct;
    return ladders.find(l => l.ladderTypeId === this.activeLadderTypeId) || ladders[0];
  }

  // Gradient bar calculations for active ladder
  get adoptionLevelsToDisplay(): LadderStage[] {
    return [...(this.activeLadder?.allStages || [])].sort((a, b) => b.rank - a.rank);
  }

  get overlayHeight(): number {
    return this.indicatorPosition;
  }

  get indicatorPosition(): number {
    const ladder = this.activeLadder;
    if (!ladder?.currentStageId || !ladder.allStages.length) return 0;
    const sorted = [...ladder.allStages].sort((a, b) => b.rank - a.rank);
    const idx = sorted.findIndex(s => s.stageId === ladder.currentStageId);
    if (idx === -1 || sorted.length <= 1) return 0;
    return (idx / (sorted.length - 1)) * 100;
  }

  get indicatorColor(): string {
    return this.stageColor(this.indicatorPosition);
  }

  // ── Goal stage (OMNI-61184) ─────────────────────────────────────────────────
  // Goal stage comes per customer/product/ladder-type row from
  // indskr_productrating.indskr_targetstage — optional, so no value means no marker. Shown as a
  // dashed marker on its stage row, separate from the solid current-stage highlight.

  isGoalStage(stage: LadderStage): boolean {
    const goalId = this.activeLadder?.goalStageId;
    return !!goalId && stage.stageId === goalId;
  }

  isGoalReached(stage: LadderStage): boolean {
    return this.isGoalStage(stage) && stage.stageId === this.activeLadder?.currentStageId;
  }

  /** Goal marker is colored to match the bar's gradient at the row the goal stage actually sits
   * on. Deliberately mirrors indicatorPosition/stageColor (index within adoptionLevelsToDisplay,
   * the same list that drives each row's top:% placement) rather than normalizing by raw rank
   * value — rank values aren't guaranteed evenly spaced, so a value-based normalization (like
   * historyDisplayEntries' dotColor, a different, unrelated calculation) can land a stage that's
   * visually near the top at a color from near the bottom of the range. */
  goalStageColor(stage: LadderStage): string {
    const stages = this.adoptionLevelsToDisplay;
    const idx = stages.findIndex(s => s.stageId === stage.stageId);
    if (idx === -1 || stages.length <= 1) return this.stageColor(0);
    return this.stageColor((idx / (stages.length - 1)) * 100);
  }

  private rankNormalizedColor(normalized: number): string {
    if (normalized >= 0.8) return '#25BB76';
    if (normalized >= 0.6) return '#B6D91A';
    if (normalized >= 0.4) return '#F5B619';
    if (normalized >= 0.2) return '#ED7211';
    return '#E11800';
  }

  private stageColor(pos: number): string {
    if (pos < 40) return '#25BB76';
    if (pos < 60) return '#F5B619';
    if (pos < 80) return '#ED7211';
    return '#E11800';
  }

  get pendingDialogTopPx(): number {
    return 18 + (this.pendingIndicatorPosition / 100) * 250;
  }

  get pendingIndicatorPosition(): number {
    if (!this.pendingStage || !this.adoptionLevelsToDisplay.length) return 0;
    const sorted = this.adoptionLevelsToDisplay;
    const idx = sorted.findIndex(l => l.stageId === this.pendingStage!.stageId);
    if (idx < 0) return 0;
    return sorted.length > 1 ? (idx / (sorted.length - 1)) * 100 : 50;
  }

  get ladderTrend(): 'up' | 'down' | null {
    if (!this.currentLadderHistory?.length) return null;
    const dir = this.currentLadderHistory[0].direction;
    return dir === 'up' ? 'up' : dir === 'down' ? 'down' : null;
  }

  get stepChartData(): { path: string; points: Array<{ x: number; y: number; label: string; date: string; user: string; dir: string }> } | null {
    const history = this.currentLadderHistory;
    const ladder = this.activeLadder;
    if (!history?.length || !ladder) return null;

    const chronological = [...history].reverse();
    const allRanks = ladder.allStages.map(s => s.rank);
    const minRank = Math.min(...allRanks);
    const maxRank = Math.max(...allRanks);
    const rankRange = maxRank - minRank || 1;

    const ptSpacing = 110;
    const chartH = 70;
    const padTop = 8;
    const padBot = 18;
    const padLeft = 20;
    const plotH = chartH - padTop - padBot;

    const toY = (rank: number) => padTop + ((maxRank - rank) / rankRange) * plotH;

    const points = chronological.map((entry, i) => ({
      x: padLeft + i * ptSpacing,
      y: toY(entry.newStageRank),
      label: entry.newStageName,
      date: entry.changedOn,
      user: entry.changedByUserName,
      dir: entry.direction || '',
    }));

    let path = '';
    points.forEach((pt, i) => {
      if (i === 0) {
        path += `M ${pt.x},${pt.y}`;
      } else {
        path += ` H ${pt.x} V ${pt.y}`;
      }
    });

    return { path, points };
  }

  get stepChartWidth(): number {
    const count = this.currentLadderHistory?.length || 0;
    return Math.max(300, 40 + count * 110);
  }

  get isOffline(): boolean {
    return this.deviceService.isOffline;
  }

  get historyCount(): number {
    return this.currentLadderHistory?.length || 0;
  }

  get historyDisplayEntries(): Array<LadderHistoryEntry & { durationLabel: string; dotColor: string; userInitials: string }> {
    const history = this.currentLadderHistory;
    const ladder = this.activeLadder;
    if (!history?.length || !ladder) return [];

    const allRanks = ladder.allStages.map(s => s.rank);
    const minRank = allRanks.length ? Math.min(...allRanks) : 0;
    const maxRank = allRanks.length ? Math.max(...allRanks) : 1;
    const rankRange = maxRank - minRank || 1;
    const now = new Date();

    const msPerDay = 24 * 60 * 60 * 1000;
    const relativeDuration = (from: Date, to: Date, isCurrent: boolean): string => {
      const diffMs = to.getTime() - from.getTime();
      const days = Math.round(diffMs / msPerDay);
      const weeks = Math.round(days / 7);
      const months = Math.round(days / 30.44);
      const suffix = isCurrent ? ' so far' : '';
      if (days < 1) return isCurrent ? 'Today' : 'Same day';
      if (days === 1) return `1 day${suffix}`;
      if (days < 7) return `${days} days${suffix}`;
      if (weeks < 5) return `${weeks} wk${suffix}`;
      return `${months} mo${suffix}`;
    };

    return history.map((entry, i) => {
      const entryDate = new Date(entry.changedOn);
      const newerDate = i === 0 ? now : new Date(history[i - 1].changedOn);
      const durationLabel = relativeDuration(entryDate, newerDate, i === 0);

      const normalized = (entry.newStageRank - minRank) / rankRange;
      const dotColor = this.rankNormalizedColor(normalized);

      const nameParts = (entry.changedByUserName || '').trim().split(/\s+/);
      const userInitials = nameParts.length >= 2
        ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
        : (nameParts[0]?.slice(0, 2) || '??').toUpperCase();

      return { ...entry, durationLabel, dotColor, userInitials };
    });
  }

  get chronologicalHistoryEntries() {
    return [...this.historyDisplayEntries].reverse();
  }

  // ── Product selection ────────────────────────────────────────────────────────

  selectProduct(product: ProductWithMultipleLadders) {
    if (!product || product.selected) return;
    this.productsWithMultipleLadders.forEach(p => (p.selected = false));
    product.selected = true;
    this.hasActiveAdoptionLadderInProductRating = false;
    this.hasSuggestionsDataInProductRating = false;
    this.hasPerformanceMetrics = false;
    this.pendingStage = null;
    this.showHistoryView = false;

    const firstLadder = this.visibleLaddersForSelectedProduct[0];
    this.activeLadderTypeId = firstLadder?.ladderTypeId || '';
    this.currentLadderHistory = firstLadder?.history || [];

    this.updateDashboardData();
    this.updatePerformanceMetrics(this.selectedDuration, product.id);
    this.cd.markForCheck();
  }

  private updateDashboardData() {
    const primaryRatingId = this.activeLadder?.productRatingId || this.visibleLaddersForSelectedProduct[0]?.productRatingId || '';
    this.suggestionDataToDisplay = this.mapSuggestionDataToDisplay(primaryRatingId);
    this.updateHasActiveAdoptionLadder();
  }

  private updateHasActiveAdoptionLadder() {
    this.hasActiveAdoptionLadderInProductRating = this.visibleLaddersForSelectedProduct.some(l => !!l.currentStageId);
  }

  // ── Ladder type selection ────────────────────────────────────────────────────

  selectLadderType(ladderTypeId: string) {
    if (this.activeLadderTypeId === ladderTypeId) return;
    this.pendingStage = null;
    this.showHistoryView = false;
    this.activeLadderTypeId = ladderTypeId;
    this.currentLadderHistory = this.activeLadder?.history || [];
    this.updateDashboardData();
  }

  // ── Stage click-to-update ────────────────────────────────────────────────────

  onStageLabelClick(stage: LadderStage) {
    if (!this.activeLadder?.isEditable) return;
    if (this.deviceService.isOffline) {
      this.notificationService.notify(
        this.translate.instant('DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED'),
        'Journey', 'top', ToastStyle.DANGER, 2000, true
      );
      return;
    }
    if (stage.stageId === this.activeLadder.currentStageId) {
      this.pendingStage = null;
      return;
    }
    this.pendingStage = stage;
  }

  cancelStageUpdate() {
    this.pendingStage = null;
  }

  async confirmStageUpdate() {
    if (!this.pendingStage || !this.activeLadder) return;
    if (this.deviceService.isOffline) {
      this.notificationService.notify(
        this.translate.instant('DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED'),
        'Journey', 'top', ToastStyle.DANGER, 2000, true
      );
      this.pendingStage = null;
      return;
    }
    const stage = this.pendingStage;
    this.pendingStage = null;
    await this.setStage(this.activeLadder, stage);
  }

  async toggleHistoryView() {
    if (!this.showHistoryView && this.deviceService.isOffline) {
      this.notificationService.notify(
        this.translate.instant('DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED'),
        'Journey', 'top', ToastStyle.DANGER, 2000, true
      );
      return;
    }
    this.showHistoryView = !this.showHistoryView;
    if (this.showHistoryView) {
      const ladder = this.activeLadder;
      if (ladder && ladder.history === null) {
        await this.loadLadderHistory(ladder);
      } else if (ladder?.history) {
        this.currentLadderHistory = ladder.history;
      }
    }
  }

  private async setStage(ladder: LadderDataPerType, stage: LadderStage) {
    const prevId = ladder.currentStageId;
    const prevName = ladder.currentStageName;
    const prevRank = ladder.currentStageRank;
    ladder.currentStageId = stage.stageId;
    ladder.currentStageName = stage.stageName;
    ladder.currentStageRank = stage.rank;
    ladder.history = null;
    this.currentLadderHistory = [];
    this.updateHasActiveAdoptionLadder();
    this.cd.markForCheck();
    try {
      await this.adoptionLadderJourneyService.updateLadderStage(ladder.productRatingId, stage.stageId);
    } catch {
      ladder.currentStageId = prevId;
      ladder.currentStageName = prevName;
      ladder.currentStageRank = prevRank;
      this.updateHasActiveAdoptionLadder();
      this.cd.markForCheck();
    }
  }

  // ── History ──────────────────────────────────────────────────────────────────

  async loadLadderHistory(ladder: LadderDataPerType) {
    if (!ladder.productRatingId) return;
    const history = await this.adoptionLadderJourneyService.fetchLadderStageHistory(ladder.productRatingId);
    ladder.history = history;
    if (ladder.ladderTypeId === this.activeLadderTypeId || this.activeLadderTypeId === '') {
      this.currentLadderHistory = history;
    }
    this.cd.markForCheck();
  }

  // ── Product mapping ──────────────────────────────────────────────────────────

  mapProductsWithMultipleLadders(productSegmentations: any[]): ProductWithMultipleLadders[] {
    if (!productSegmentations?.length) return [];

    // Group adoption stages by ladder type ID (from fetchAdoptionLadderData aliases)
    const stagesByType = new Map<string, LadderStage[]>(); // keyed by typeId or 'default'
    this.rawAdoptionLadderLevel.forEach(stage => {
      const typeId = stage['ladderTypeId'] || 'default';
      if (!stagesByType.has(typeId)) stagesByType.set(typeId, []);
      stagesByType.get(typeId)!.push({
        stageId: stage.indskr_adoptionladderid,
        stageName: stage.indskr_name,
        rank: stage.indskr_rank,
        indexedRank: 0,
      });
    });
    stagesByType.forEach((stages, typeId) => {
      const sorted = stages.sort((a, b) => a.rank - b.rank);
      sorted.forEach((s, i) => (s.indexedRank = i));
      stagesByType.set(typeId, sorted);
    });

    const productMap = new Map<string, ProductWithMultipleLadders>();

    productSegmentations.forEach(seg => {
      if (!seg.productId || !seg.productName) return;
      if (!seg.ladderTypeId && !seg.currentStageId) return;

      if (!productMap.has(seg.productId)) {
        productMap.set(seg.productId, {
          id: seg.productId,
          name: seg.productName,
          selected: false,
          ladders: [],
        });
      }

      const product = productMap.get(seg.productId)!;
      const typeId: string = seg.ladderTypeId || 'default';

      const existingLadder = product.ladders.find(l => l.ladderTypeId === typeId);
      if (existingLadder) {
        if (Number(seg.accessType) === 268890001) {
          existingLadder.isEditable = true;
        }
        // Duplicates here are one productrating repeated by the role-mapping outer join.
        if (!existingLadder.goalStageId && seg.goalStageId) {
          existingLadder.goalStageId = seg.goalStageId;
        }
        return;
      }

      const isEditable = Number(seg.accessType) === 268890001;

      product.ladders.push({
        productRatingId: seg.productRatingId || '',
        ladderTypeId: typeId,
        ladderTypeName: seg.ladderTypeName || this.translate.instant('ADOPTION_LADDER'),
        isEditable,
        isVisible: true,
        currentStageId: seg.currentStageId || '',
        currentStageName: seg.currentStageName || '',
        currentStageRank: Number(seg.currentStageRank) || 0,
        goalStageId: seg.goalStageId || '',
        allStages: stagesByType.get(typeId) || [],
        history: null,
      });
    });

    return Array.from(productMap.values()).filter(p => p.ladders.length > 0);
  }

  // ── RxSales mapping ──────────────────────────────────────────────────────────

  private mapFormattedRxSalesDataRealTimeFetched(): any {
    if (!this.rawContactRxSalesDataRealTimeFetched?.length) return {};
    const performanceData: any = {};
    this.rawContactRxSalesDataRealTimeFetched.forEach((item: any) => {
      const rawProductId = item['_indskr_product_value'];
      const rawPeriod = item['rxsalesdataattribute.indskr_period'];
      const rawMetric = item['rxsalesdataattribute.indskr_metric'];
      const rawValue = item['rxsalesdataattribute.indskr_value'];
      const rawTrendValue = item['rxsalesdataattribute.indskr_changefromlastperiod'];
      if (!rawProductId || !rawPeriod || !rawMetric || rawValue === undefined) return;
      const periodKey = this.getPeriodKey(rawPeriod);
      if (!periodKey) return;
      if (!performanceData[rawProductId]) performanceData[rawProductId] = {};
      if (!performanceData[rawProductId][periodKey]) {
        performanceData[rawProductId][periodKey] = this.entityType === 'account'
          ? { salesValue: { value: 0, trendValue: null, formattedTrendValue: '' }, salesVolume: { value: 0, trendValue: null, formattedTrendValue: '' } }
          : { nrx: { value: 0, trendValue: null, formattedTrendValue: '' }, refills: { value: 0, trendValue: null, formattedTrendValue: '' }, trx: { value: 0, trendValue: null, formattedTrendValue: '' } };
      }
      const metricKey = this.getMetricKey(rawMetric);
      if (metricKey) {
        performanceData[rawProductId][periodKey][metricKey] = {
          value: rawValue,
          trendValue: rawTrendValue,
          formattedTrendValue: this.formatDisplayTrendValue(rawTrendValue),
        };
      }
    });
    return performanceData;
  }

  private mapFormattedRxSalesData(): any {
    if (!this.rawContactRxSalesData?.length) return {};
    const performanceData: any = {};
    this.rawContactRxSalesData.forEach((item: any) => {
      const productId = item?.product?.ID;
      const period = item?.period;
      if (!productId || !period) return;
      const periodKey = this.getPeriodKey(period);
      if (!periodKey) return;
      if (!performanceData[productId]) performanceData[productId] = {};
      if (!performanceData[productId][periodKey]) {
        performanceData[productId][periodKey] = this.entityType === 'account'
          ? { salesValue: { value: 0, trendValue: null, formattedTrendValue: '' }, salesVolume: { value: 0, trendValue: null, formattedTrendValue: '' } }
          : { nrx: { value: 0, trendValue: null, formattedTrendValue: '' }, refills: { value: 0, trendValue: null, formattedTrendValue: '' }, trx: { value: 0, trendValue: null, formattedTrendValue: '' } };
      }
      if (this.entityType === 'account') {
        const sv = item?.salesValue;
        const svol = item?.salesVolume;
        if (sv) performanceData[productId][periodKey].salesValue = { value: sv.value || 0, trendValue: sv.change || null, formattedTrendValue: this.formatDisplayTrendValue(sv.change) };
        if (svol) performanceData[productId][periodKey].salesVolume = { value: svol.value || 0, trendValue: svol.change || null, formattedTrendValue: this.formatDisplayTrendValue(svol.change) };
      } else {
        const trx = item?.TRx;
        const nrx = item?.NRx;
        const ref = item?.Refills;
        if (trx) performanceData[productId][periodKey].trx = { value: trx.value || 0, trendValue: trx.change || null, formattedTrendValue: this.formatDisplayTrendValue(trx.change) };
        if (nrx) performanceData[productId][periodKey].nrx = { value: nrx.value || 0, trendValue: nrx.change || null, formattedTrendValue: this.formatDisplayTrendValue(nrx.change) };
        if (ref) performanceData[productId][periodKey].refills = { value: ref.value || 0, trendValue: ref.change || null, formattedTrendValue: this.formatDisplayTrendValue(ref.change) };
      }
    });
    return performanceData;
  }

  private getPeriodKey(period: number): string | null {
    return Period[period] || null;
  }

  private getMetricKey(metric: number): string | null {
    switch (metric) {
      case Metric.NRx: return 'nrx';
      case Metric.Refills: return 'refills';
      case Metric.TRx: return 'trx';
      case Metric.SalesValue: return 'salesValue';
      case Metric.SalesVolume: return 'salesVolume';
      default: return null;
    }
  }

  // ── Suggestion mapping ───────────────────────────────────────────────────────

  private mapSuggestionDataToDisplay(foundProductRatingId: string): JourneySuggestionCategory[] {
    if (!foundProductRatingId) return [];
    const selSuggestionData = this.mapSuggestionDataToStructuredFormat[foundProductRatingId];
    const objectiveName = this.translate.instant('JOURNEY_OBJECTIVE');
    const reachName = this.translate.instant('JOURNEY_REACH');
    const depthName = this.translate.instant('JOURNEY_DEPTH');
    const interactionsName = this.translate.instant('JOURNEY_INTERACTIONS');
    const keyMessagesName = this.translate.instant('JOURNEY_KEY_MESSAGES');
    const contentName = this.translate.instant('JOURNEY_CONTENT');

    if (selSuggestionData) {
      this.hasSuggestionsDataInProductRating =
        !!(selSuggestionData.objective?.length || selSuggestionData.reach?.length ||
          selSuggestionData.depth?.length || selSuggestionData.interactions?.length ||
          selSuggestionData.keyMessages?.length || selSuggestionData.content?.length);
    } else {
      this.hasSuggestionsDataInProductRating = false;
    }

    if (!selSuggestionData) {
      return [
        { type: JourneySuggestionType.OBJECTIVE, categoryName: objectiveName, items: [] },
        { type: JourneySuggestionType.REACH, categoryName: reachName, items: [] },
        { type: JourneySuggestionType.DEPTH, categoryName: depthName, items: [] },
        { type: JourneySuggestionType.INTERACTION, categoryName: interactionsName, items: [] },
        { type: JourneySuggestionType.KEY_MESSAGE, categoryName: keyMessagesName, items: [] },
      ];
    }

    let formattedContent: ContentItem = { contentListData: [], valuePopupText: '', popupListData: [] };
    if (selSuggestionData.content?.length > 0) {
      const maxToDisplay = 5;
      formattedContent = {
        contentListData: selSuggestionData.content.slice(0, maxToDisplay),
        valuePopupText: selSuggestionData.content.length > maxToDisplay ? `+ ${selSuggestionData.content.length - maxToDisplay}` : '',
        popupListData: selSuggestionData.content.length > maxToDisplay ? selSuggestionData.content.slice(maxToDisplay) : [],
      };
    }

    const categories: JourneySuggestionCategory[] = [
      { type: JourneySuggestionType.OBJECTIVE, categoryName: objectiveName, items: selSuggestionData.objective || [] },
      { type: JourneySuggestionType.REACH, categoryName: reachName, items: selSuggestionData.reach || [] },
      { type: JourneySuggestionType.DEPTH, categoryName: depthName, items: selSuggestionData.depth || [] },
      { type: JourneySuggestionType.INTERACTION, categoryName: interactionsName, items: selSuggestionData.interactions || [] },
      { type: JourneySuggestionType.KEY_MESSAGE, categoryName: keyMessagesName, items: selSuggestionData.keyMessages || [] },
    ];
    if (formattedContent.contentListData.length > 0) {
      categories.push({ type: JourneySuggestionType.CONTENT, categoryName: contentName, items: formattedContent });
    }
    return categories;
  }

  private mapRawSuggestionDataToStructuredFormat(rawSuggestionData: any[]): { [id: string]: any } {
    if (!Array.isArray(rawSuggestionData) || rawSuggestionData.length === 0) return {};
    const structuredData: { [id: string]: any } = {};

    for (const item of rawSuggestionData) {
      const ratingId = item['_indskr_customersegmentation_value'] || null;
      const suggestionType = item['indskr_suggestiontype'] || null;
      if (!ratingId || !suggestionType) continue;

      if (!structuredData[ratingId]) {
        structuredData[ratingId] = { objective: [], reach: [], depth: [], interactions: [], keyMessages: [], content: [] };
      }
      const seg = structuredData[ratingId];
      const addUnique = (arr: any[], id: string, name: string) => {
        if (id && !arr.some((el: any) => el.id === id)) arr.push({ id, name });
      };

      switch (suggestionType) {
        case JourneySuggestionType.OBJECTIVE: addUnique(seg.objective, item['masterdata.indskr_masterdataid'] || '', item['masterdata.indskr_name'] || ''); break;
        case JourneySuggestionType.REACH: addUnique(seg.reach, item['masterdata.indskr_masterdataid'] || '', item['masterdata.indskr_name'] || ''); break;
        case JourneySuggestionType.DEPTH: addUnique(seg.depth, item['masterdata.indskr_masterdataid'] || '', item['masterdata.indskr_name'] || ''); break;
        case JourneySuggestionType.INTERACTION: addUnique(seg.interactions, item['masterdata.indskr_masterdataid'] || '', item['masterdata.indskr_name'] || ''); break;
        case JourneySuggestionType.KEY_MESSAGE: addUnique(seg.keyMessages, item['keymessage.indskr_keymessageid'] || '', item['keymessage.indskr_name'] || ''); break;
        case JourneySuggestionType.CONTENT: this.mapContentToStructuredData(seg, item); break;
      }
    }
    return structuredData;
  }

  private mapContentToStructuredData(segmentationData: any, item: any): void {
    const addContent = (id: string, title: string, thumbUrl: string, detailType: string) => {
      if (id && title && !segmentationData.content?.find((c: any) => c.id === id)) {
        segmentationData.content.push({ id, title, detailObjectType: detailType, detailObjectId: id, thumbnailURL: thumbUrl });
      }
    };
    addContent(item['iodocument.indskr_iodocumentid'] || '', item['iodocument.indskr_title'] || '', item['iodocument.indskr_ckmthumbnailurl'] || '../../../../assets/imgs/thumbnail_default.png', 'ActivityResource');
    addContent(item['iopresentation.indskr_iopresentationid'] || '', item['iopresentation.indskr_name'] || item['iopresentation.indskr_title'] || '', item['iopresentation.indskr_ckmthumbnailurl'] || '../../../../assets/imgs/thumbnail_default.png', 'ActivityPresentation');
    addContent(item['ioresource.indskr_ioresourceid'] || '', item['ioresource.indskr_title'] || '', item['ioresource.indskr_ckmthumbnailurl'] || '../../../../assets/imgs/thumbnail_default.png', 'ActivityResource');
  }

  // ── RxSales product mapping ──────────────────────────────────────────────────

  private mapRealTimeFetchedRxSalesDataToProducts(rxSalesData: any[]): ProductWithAdoptionLadder[] {
    if (!rxSalesData?.length) return [];
    return uniqBy(
      rxSalesData.map((item: any) => ({
        id: item['_indskr_product_value'],
        name: item['_indskr_product_value_Formatted'] || item['_indskr_product_value@OData.Community.Display.V1.FormattedValue'],
        productRatingId: '',
        adoptionLadderId: '',
        adoptionLadderRank: 0,
        adoptionLadderName: '',
        selected: false,
      } as ProductWithAdoptionLadder)),
      'id',
    );
  }

  private mapRxSalesDataToProducts(rxSalesData: any[]): ProductWithAdoptionLadder[] {
    if (!rxSalesData?.length) return [];
    return uniqBy(
      rxSalesData.map((item: any) => ({
        id: item['product']['ID'],
        name: item['product']['name'],
        productRatingId: '',
        adoptionLadderId: '',
        adoptionLadderRank: 0,
        adoptionLadderName: '',
        selected: false,
      } as ProductWithAdoptionLadder)),
      'id',
    );
  }

  // ── Performance metrics ──────────────────────────────────────────────────────

  onDurationChange() {
    const selected = this.selectedProduct;
    if (selected) this.updatePerformanceMetrics(this.selectedDuration, selected.id);
  }

  private updatePerformanceMetrics(duration: Period, productId: string) {
    this.performanceMetricsToDisplay = this.mapPerformanceMetricsToDisplay(this.structuredPerformanceData, duration, productId);
  }

  private mapPerformanceMetricsToDisplay(formattedPerformanceData: any, duration: Period, productId: string): PerformanceMetricDisplay[] {
    const periodKey = this.getPeriodKey(duration);
    if (!periodKey || !formattedPerformanceData[productId]) {
      this.hasPerformanceMetrics = false;
      return this.getDefaultPerformanceMetrics();
    }
    this.hasPerformanceMetrics = true;
    const data = formattedPerformanceData[productId][periodKey];
    if (!data) return this.getDefaultPerformanceMetrics();

    if (this.entityType === 'account') {
      return [
        { label: this.translate.instant('SALES_DATA_TOTAL_VALUE'), value: this.formatDisplayValue(data.salesValue?.value || 0), trendValue: data.salesValue?.trendValue || null, formattedTrendValue: data.salesValue?.formattedTrendValue || '' },
        { label: this.translate.instant('SALES_DATA_TOTAL_VOLUME'), value: this.formatDisplayValue(data.salesVolume?.value || 0), trendValue: data.salesVolume?.trendValue || null, formattedTrendValue: data.salesVolume?.formattedTrendValue || '' },
      ];
    }
    return [
      { label: this.translate.instant('SALES_DATA_NRX'), value: this.formatDisplayValue(data.nrx?.value || 0), trendValue: data.nrx?.trendValue || null, formattedTrendValue: data.nrx?.formattedTrendValue || '' },
      { label: this.translate.instant('SALES_DATA_REFILLS'), value: this.formatDisplayValue(data.refills?.value || 0), trendValue: data.refills?.trendValue || null, formattedTrendValue: data.refills?.formattedTrendValue || '' },
      { label: this.translate.instant('SALES_DATA_TRX'), value: this.formatDisplayValue(data.trx?.value || 0), trendValue: data.trx?.trendValue || null, formattedTrendValue: data.trx?.formattedTrendValue || '' },
    ];
  }

  private getDefaultPerformanceMetrics(): PerformanceMetricDisplay[] {
    if (this.entityType === 'account') {
      return [
        { label: this.translate.instant('SALES_DATA_TOTAL_VALUE'), value: '0', trendValue: null, formattedTrendValue: '' },
        { label: this.translate.instant('SALES_DATA_TOTAL_VOLUME'), value: '0', trendValue: null, formattedTrendValue: '' },
      ];
    }
    return [
      { label: this.translate.instant('SALES_DATA_NRX'), value: '0', trendValue: null, formattedTrendValue: '' },
      { label: this.translate.instant('SALES_DATA_REFILLS'), value: '0', trendValue: null, formattedTrendValue: '' },
      { label: this.translate.instant('SALES_DATA_TRX'), value: '0', trendValue: null, formattedTrendValue: '' },
    ];
  }

  private formatDisplayValue(value: number): string {
    if (!value || value === 0) return '0';
    if (value >= 1000000) return (Math.round(value / 100000) / 10).toFixed(1).replace(/\.0$/, '') + 'M';
    if (value >= 1000) return (Math.round(value / 100) / 10).toFixed(1).replace(/\.0$/, '') + 'K';
    return value.toString();
  }

  private formatDisplayTrendValue(value: number): string {
    if (!value || value === 0) return '';
    return Math.round(Math.abs(value) * 10) / 10 + '%';
  }

  // ── Content details ──────────────────────────────────────────────────────────

  public openDetails(items: any) {
    if (items?.detailObjectType && items?.detailObjectId) {
      this._openContentDetails(items.detailObjectType, items.detailObjectId);
    }
  }

  private _openContentDetails(contentType: string, contentId: string) {
    if (!contentType || !contentId) return;
    if (contentType === 'ActivityPresentation') {
      const pres = this.presentationService.getPresentationById(contentId);
      if ((this.deviceService.isOffline || this.deviceService.isDeviceRealOffline) && !pres?.downloaded) {
        this.notificationService.notify(this.translate.instant('PLEASE_DOWNLOAD_THE_PRESENATION_TO_VIEW_WHILE_OFFLINE'), 'Presentation List', 'top', ToastStyle.DANGER, 2000, true);
        return;
      }
      if (pres) {
        this.presentationService.setCarouselBriefcase([pres]);
        this.presentationService.setCurrentSelectedPres(pres);
        let page: any = this.presentationService.presPages?.[0] ?? null;
        if (pres.customePageLocation) {
          page = isNaN(parseInt(pres.customePageLocation))
            ? (this.presentationService.presPages?.find(o => o.name === pres.customePageLocation) ?? null)
            : (this.presentationService.presPages?.find(o => o.name === 'Slide' + pres.customePageLocation) ?? null);
        }
        this.presentationService.setCurrentSelectedPresPage(page);
        // Navigate by PageName so NavigationService resolves the component from the lazy
        // component-registry (component-registrations.ts) — passing the class directly would
        // re-introduce an eager SharedModule → AdoptionLadderJourney → PresentationPreview →
        // IOCarousel → SharedModule import cycle (crashes `ng serve` with a SharedModule TDZ).
        this.navService.pushChildNavPageWithPageTracking(PageName.PresentationPreviewComponent, PageName.PresentationPreviewComponent, this.navService.getCurrentMasterPageName(), { from: 'AdoptionLadderJourneyComponent', viewMode: PresentationView.MENU });
      }
    } else if (contentType === 'ActivityResource') {
      const resource = this.resourceService.getResourceById(contentId, contentId);
      if (resource) {
        if ((this.deviceService.isOffline || this.deviceService.isDeviceRealOffline) && !resource.downloaded) {
          this.notificationService.notify(this.translate.instant('PLEASE_DOWNLOAD_THE_PRESENATION_TO_VIEW_WHILE_OFFLINE'), 'Presentation List', 'top', ToastStyle.DANGER, 2000, true);
          return;
        }
        this.navService.pushChildNavPageWithPageTracking(ResourceDetailsComponent, PageName.ResourceDetailsComponent, this.navService.getCurrentMasterPageName(), { from: 'AdoptionLadderJourneyComponent' });
        this.resourceService.highlightResource(resource);
      }
    }
  }

  async openPopup(ev: any, popupData: any) {
    if (!popupData?.length) return;
    const popover = await this.popoverController.create({
      component: ListPreviewComponent,
      cssClass: 'list-preview-popover',
      event: ev,
      translucent: true,
      componentProps: { popupListData: popupData },
    });
    await popover.present();
    popover.onDidDismiss().then((data: any) => {
      if (data?.data?.contentType && data?.data?.contentId) {
        this._openContentDetails(data.data.contentType, data.data.contentId);
      }
    });
  }

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
